import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, Boxes, ShieldCheck, Truck, Home, ShoppingCart } from "lucide-react";

const STAGES = [
  { key: "confirmed",  label: "Order Received", icon: ShoppingCart },
  { key: "processing", label: "Processing",     icon: Boxes },
  { key: "packed",     label: "Packed",         icon: ShieldCheck },
  { key: "shipped",    label: "Dispatched",     icon: Truck },
  { key: "delivered",  label: "Delivered",      icon: Home },
];

function stageIndex(order) {
  const s = (order.delivery_status || "processing").toLowerCase();
  const map = { processing: 1, packed: 2, shipped: 3, delivered: 4 };
  return map[s] ?? 0;
}

export default function TrackOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders((data || []).filter((o) => (o.delivery_status || "").toLowerCase() !== "delivered"));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Track Orders">
      {loading ? (
        <div className="text-slate-500">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          You have no orders in transit. Head to <strong className="text-brand-blue">Place Order</strong> to start shopping.
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((o) => {
            const idx = stageIndex(o);
            const eta = new Date(new Date(o.created_at).getTime() + 5 * 24 * 60 * 60 * 1000);
            return (
              <div key={o.id} data-testid={`track-${o.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
                  <div>
                    <div className="text-xs text-slate-500">Order</div>
                    <div className="font-heading font-semibold text-brand-navy">{o.order_number}</div>
                  </div>
                  <div className="text-xs text-slate-500">
                    Placed {new Date(o.created_at).toLocaleDateString('en-IN')}
                  </div>
                  <div className="text-xs text-slate-500">
                    Estimated delivery: <span className="text-brand-navy font-semibold">
                      {eta.toLocaleDateString('en-IN')}
                    </span>
                  </div>
                </div>
                {/* Timeline */}
                <div className="grid grid-cols-5 gap-2">
                  {STAGES.map((s, i) => {
                    const done = i <= idx;
                    const current = i === idx;
                    return (
                      <div key={s.key} className="text-center">
                        <div className={`mx-auto w-10 h-10 rounded-full grid place-items-center border-2 ${
                          done ? "bg-brand-green border-brand-green text-white" :
                          current ? "bg-white border-brand-blue text-brand-blue animate-pulse" :
                          "bg-white border-slate-200 text-slate-400"
                        }`}>
                          <s.icon size={16} />
                        </div>
                        <div className={`mt-2 text-[11px] ${done ? "text-brand-navy font-semibold" : "text-slate-500"}`}>
                          {s.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
